import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    if (dialog) {
      dialog.addEventListener('close', this.#resetBodyOnClose);
    }
    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { dialog } = this.refs || {};
    if (dialog) {
      dialog.removeEventListener('close', this.#resetBodyOnClose);
    }
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  /**
   * Setzt bei einem Sheet-Dialog alle Scroll-Positionen zurück (Dialog, Inner, verschachtelte scrollbare Elemente),
   * damit beim erneuten Öffnen der Inhalt immer oben startet.
   * @param {HTMLDialogElement} dialog
   */
  #resetSheetScrollState(dialog) {
    if (!dialog?.classList.contains('popup-link__content--sheet')) return;
    dialog.scrollTop = 0;
    const inner = dialog.querySelector('.popup-link__inner');
    if (inner) {
      inner.scrollTop = 0;
      const descendants = inner.querySelectorAll('*');
      for (let i = 0; i < descendants.length; i++) {
        const el = descendants[i];
        if (el && el.scrollHeight > el.clientHeight) el.scrollTop = 0;
      }
    }
  }

  /**
   * Stellt Body-Styles wieder her, wenn das Dialog (auch nativ) geschlossen wird.
   * Bei Sheet: Scroll-Zustand zurücksetzen.
   */
  #resetBodyOnClose = () => {
    const { dialog } = this.refs || {};
    this.#resetSheetScrollState(dialog);
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   * Scroll-Lock nur per html[scroll-lock] (overflow: hidden), ohne body zu verschieben,
   * damit der Header im Hintergrund sichtbar bleibt und nicht „eingefahren“ wirkt.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    requestAnimationFrame(() => {
      document.documentElement.setAttribute('scroll-lock', '');

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   * Bei Sheet: Inline-Transform entfernen, damit CSS-Close-Animation läuft; auf transitionend warten.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    if (this._dialogClosing) return;
    this._dialogClosing = true;

    try {
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleKeyDown);

      const isSheet = dialog.classList.contains('popup-link__content--sheet');
      if (isSheet) {
        dialog.style.transition = '';
        dialog.style.transform = '';
      }

      dialog.classList.add('dialog-closing');

      if (isSheet) {
        await new Promise((resolve) => {
          const done = () => {
            dialog.removeEventListener('transitionend', onTransitionEnd);
            clearTimeout(timeoutId);
            resolve(undefined);
          };
          /** @param {TransitionEvent} e */
          const onTransitionEnd = (e) => {
            if (e.target === dialog && e.propertyName === 'transform') done();
          };
          dialog.addEventListener('transitionend', onTransitionEnd);
          const timeoutId = setTimeout(done, 500);
        });
      } else {
        await onAnimationEnd(dialog, undefined, { subtree: false });
      }

      document.documentElement.removeAttribute('scroll-lock');

      dialog.close();
      dialog.classList.remove('dialog-closing');

      this.#resetSheetScrollState(dialog);

      this.dispatchEvent(new DialogCloseEvent());
    } finally {
      this._dialogClosing = false;
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
